<template>
    <BaseDialog v-model:open="isOpen" show-close title="Import people from CSV" dialog-content-class="max-w-2xl!">
        <div class="p-4 md:p-10 pt-0! flex flex-col gap-6">
            <!-- Step 1: Upload -->
            <template v-if="step === 'upload'">
                <p class="body-small text-gray-600">Upload a CSV file to add people in bulk. Only the <strong>name</strong> column is required.</p>

                <!-- Upload area -->
                <!-- Mobile: simple upload button -->
                <div v-if="!selectedFile" class="md:hidden">
                    <button
                        type="button"
                        class="w-full flex items-center justify-center gap-2 px-4 py-3.5 border-2 border-dashed border-valence-grey-300 rounded-xl body-small text-gray-600 hover:border-valence-grey-750 active:bg-gray-50 transition-colors"
                        @click="triggerFileInput">
                        <i class="bi bi-file-earmark-arrow-up text-lg" aria-hidden="true"></i>
                        Upload CSV file
                    </button>
                </div>
                <!-- Desktop: drag-and-drop zone -->
                <div v-if="!selectedFile" class="hidden md:block">
                    <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
                    <div
                        :class="{ 'border-valence-grey-750 bg-gray-50': isDragging }"
                        class="border-2 border-dashed border-valence-grey-300 rounded-xl p-8 text-center cursor-pointer hover:border-valence-grey-750 transition-colors"
                        role="button"
                        tabindex="0"
                        @click="triggerFileInput"
                        @keydown.enter="triggerFileInput"
                        @dragover.prevent="isDragging = true"
                        @dragleave="isDragging = false"
                        @drop.prevent="handleDrop">
                        <i class="bi bi-cloud-arrow-up text-3xl text-gray-400 mb-2 block" aria-hidden="true"></i>
                        <p class="body-small text-gray-500">Click to browse or drag and drop a CSV file</p>
                        <p class="details text-gray-400 mt-1">CSV files only, up to 1 MB</p>
                    </div>
                </div>

                <!-- File info card (shown after file selected) -->
                <div v-if="selectedFile" class="flex flex-col">
                    <div class="border border-gray-200 rounded-xl p-4 flex flex-col gap-3">
                        <div class="flex items-center gap-3">
                            <div class="rounded-full border-2 border-gray-200 min-w-10 min-h-10 flex items-center justify-center details uppercase text-gray-500">CSV</div>
                            <div class="grow min-w-0">
                                <div class="body-small-semibold truncate">{{ truncatedFileName }}</div>
                                <span class="details text-gray-500">{{ formattedFileSize }}</span>
                            </div>
                            <button
                                type="button"
                                aria-label="Remove selected file"
                                class="border border-gray-200 rounded-full flex items-center justify-center w-7 h-7 hover:bg-gray-50 transition-colors"
                                @click="clearFile">
                                <i class="bi bi-x text-gray-500 leading-none" aria-hidden="true"></i>
                            </button>
                        </div>
                        <!-- Parsed file info -->
                        <div v-if="parsedFileInfo" class="flex flex-wrap items-center gap-2 details">
                            <span class="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{{ parsedFileInfo.rowCount }} rows</span>
                            <span
                                v-for="col in parsedFileInfo.detectedColumns"
                                :key="col"
                                class="px-2 py-0.5 rounded-full"
                                :class="expectedColumns.includes(col) ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'">
                                {{ col }}
                            </span>
                            <span v-for="col in missingExpectedColumns" :key="'missing-' + col" class="px-2 py-0.5 rounded-full bg-red-50 text-red-600"> {{ col }} (missing) </span>
                        </div>
                    </div>
                    <button type="button" class="md:hidden body-small text-[#0066CC] hover:text-[#0052A3] mt-1" @click="clearFile">Replace file</button>
                </div>

                <input ref="fileInput" type="file" accept=".csv" class="hidden" aria-label="CSV file upload" @change="handleFileSelect" />

                <div v-if="uploadError" class="body-small text-red-600">{{ uploadError }}</div>

                <button type="button" class="body-small text-[#0066CC] hover:text-[#0052A3] inline-flex items-center gap-1 w-fit" @click="downloadTemplate">
                    <i class="bi bi-download" aria-hidden="true"></i>
                    Download CSV template
                </button>

                <!-- Column reference (collapsible) -->
                <div>
                    <button type="button" class="flex items-center gap-1.5 details text-gray-500 hover:text-gray-700 transition-colors" @click="columnRefOpen = !columnRefOpen">
                        <i :class="columnRefOpen ? 'bi-chevron-up' : 'bi-chevron-down'" class="bi text-[10px]" aria-hidden="true"></i>
                        View column requirements
                    </button>
                    <div v-show="columnRefOpen" class="mt-2">
                        <!-- Mobile: stacked card list -->
                        <div class="md:hidden space-y-2">
                            <div class="border border-gray-200 rounded-lg px-3 py-2.5">
                                <div class="details text-gray-900">name <span class="font-normal text-blue-600">(required)</span></div>
                                <div class="details text-gray-500 mt-0.5">Text</div>
                                <div class="details text-gray-400 mt-0.5">Example: Jane Smith</div>
                            </div>
                            <div class="border border-gray-200 rounded-lg px-3 py-2.5">
                                <div class="details text-gray-600">job_title</div>
                                <div class="details text-gray-500 mt-0.5">Text</div>
                                <div class="details text-gray-400 mt-0.5">Example: Engineering Manager</div>
                            </div>
                            <div class="border border-gray-200 rounded-lg px-3 py-2.5">
                                <div class="details text-gray-600">email</div>
                                <div class="details text-gray-500 mt-0.5">Email (used for duplicate detection)</div>
                                <div class="details text-gray-400 mt-0.5">Example: jane@example.com</div>
                            </div>
                            <div class="border border-gray-200 rounded-lg px-3 py-2.5">
                                <div class="details text-gray-600">in_team</div>
                                <div class="details text-gray-500 mt-0.5">yes / no</div>
                                <div class="details text-gray-400 mt-0.5">Example: yes</div>
                            </div>
                            <div class="border border-gray-200 rounded-lg px-3 py-2.5">
                                <div class="details text-gray-600">in_org</div>
                                <div class="details text-gray-500 mt-0.5">yes / no</div>
                                <div class="details text-gray-400 mt-0.5">Example: yes</div>
                            </div>
                            <div class="border border-gray-200 rounded-lg px-3 py-2.5">
                                <div class="details text-gray-600">comfort_level</div>
                                <div class="details text-gray-500 mt-0.5">0–4, where 4 = Very</div>
                                <div class="details text-gray-400 mt-0.5">Example: 3</div>
                            </div>
                            <div class="border border-gray-200 rounded-lg px-3 py-2.5">
                                <div class="details text-gray-600">notes</div>
                                <div class="details text-gray-500 mt-0.5">Text</div>
                                <div class="details text-gray-400 mt-0.5">Example: Reports to VP of Eng</div>
                            </div>
                        </div>
                        <!-- Desktop: table -->
                        <div class="hidden md:block border border-gray-200 rounded-lg overflow-hidden">
                            <table class="w-full details">
                                <thead class="bg-gray-50 text-gray-500">
                                    <tr>
                                        <th class="text-left px-3 py-1.5 font-medium">Column</th>
                                        <th class="text-left px-3 py-1.5 font-medium">Required</th>
                                        <th class="text-left px-3 py-1.5 font-medium">Format</th>
                                        <th class="text-left px-3 py-1.5 font-medium">Example</th>
                                    </tr>
                                </thead>
                                <tbody class="text-gray-600">
                                    <tr class="border-t border-gray-100">
                                        <td class="px-3 py-1.5 font-semibold text-gray-900">name</td>
                                        <td class="px-3 py-1.5">Yes</td>
                                        <td class="px-3 py-1.5">Text</td>
                                        <td class="px-3 py-1.5">Jane Smith</td>
                                    </tr>
                                    <tr class="border-t border-gray-100">
                                        <td class="px-3 py-1.5 text-gray-400">job_title</td>
                                        <td class="px-3 py-1.5"></td>
                                        <td class="px-3 py-1.5">Text</td>
                                        <td class="px-3 py-1.5">Engineering Manager</td>
                                    </tr>
                                    <tr class="border-t border-gray-100">
                                        <td class="px-3 py-1.5 text-gray-400">email</td>
                                        <td class="px-3 py-1.5"></td>
                                        <td class="px-3 py-1.5">Email (used for duplicate detection)</td>
                                        <td class="px-3 py-1.5">jane@example.com</td>
                                    </tr>
                                    <tr class="border-t border-gray-100">
                                        <td class="px-3 py-1.5 text-gray-400">in_team</td>
                                        <td class="px-3 py-1.5"></td>
                                        <td class="px-3 py-1.5">yes / no</td>
                                        <td class="px-3 py-1.5">yes</td>
                                    </tr>
                                    <tr class="border-t border-gray-100">
                                        <td class="px-3 py-1.5 text-gray-400">in_org</td>
                                        <td class="px-3 py-1.5"></td>
                                        <td class="px-3 py-1.5">yes / no</td>
                                        <td class="px-3 py-1.5">yes</td>
                                    </tr>
                                    <tr class="border-t border-gray-100">
                                        <td class="px-3 py-1.5 text-gray-400">comfort_level</td>
                                        <td class="px-3 py-1.5"></td>
                                        <td class="px-3 py-1.5">0–4, where 4 = Very</td>
                                        <td class="px-3 py-1.5">3</td>
                                    </tr>
                                    <tr class="border-t border-gray-100">
                                        <td class="px-3 py-1.5 text-gray-400">notes</td>
                                        <td class="px-3 py-1.5"></td>
                                        <td class="px-3 py-1.5">Text</td>
                                        <td class="px-3 py-1.5">Reports to VP of Eng</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p class="details text-gray-400 mt-2">Tip: Save your spreadsheet as CSV (UTF-8) for best results. Column names are case-insensitive.</p>
                    </div>
                </div>

                <div class="flex justify-end gap-3 sticky bottom-0 bg-white pt-3 pb-4 md:relative md:pb-0 -mx-4 px-4 md:mx-0 md:px-0 border-t border-gray-100 md:border-t-0">
                    <BaseButton theme="tertiary" @click="isOpen = false">Cancel</BaseButton>
                    <BaseButton theme="primary" :disabled="!selectedFile || isUploading" @click="handleUpload">
                        {{ isUploading ? "Uploading..." : "Preview Import" }}
                    </BaseButton>
                </div>
            </template>

            <!-- Step 2: Preview -->
            <template v-else-if="step === 'preview'">
                <div>
                    <h3 class="body-bold text-gray-900">Review import</h3>
                    <p class="body-small text-gray-500 mt-1">Verify the data below before importing. Rows with errors will be skipped.</p>
                </div>
                <div class="flex flex-wrap gap-3 body-small text-gray-600">
                    <span class="px-2 py-1 rounded bg-green-50 text-green-700">{{ previewData.summary.new }} new</span>
                    <span class="px-2 py-1 rounded bg-amber-50 text-amber-700">{{ previewData.summary.duplicates }} duplicates</span>
                    <span v-if="previewData.summary.errors > 0" class="px-2 py-1 rounded bg-red-50 text-red-700">{{ previewData.summary.errors }} errors</span>
                </div>
                <!-- Auto-overwrite toggle -->
                <div v-if="previewData.summary.duplicates > 0" class="flex flex-col gap-1">
                    <label for="csv-auto-overwrite" class="flex items-center gap-2 body-small text-gray-600 cursor-pointer">
                        <input id="csv-auto-overwrite" v-model="autoOverwrite" type="checkbox" class="rounded border-gray-300" />
                        <span>Update all duplicates with CSV data</span>
                    </label>
                    <p class="details text-gray-400 ml-6">Existing records matching by name or email will be updated. Uncheck to choose per row.</p>
                </div>

                <!-- Mobile: stacked card list -->
                <div class="md:hidden max-h-96 overflow-y-auto space-y-2">
                    <div
                        v-for="row in previewData.rows"
                        :key="'m-' + row.row_index"
                        class="border rounded-lg p-3"
                        :class="{
                            'border-red-200 bg-red-50': row.errors.length > 0,
                            'border-amber-200 bg-amber-50': row.is_duplicate && !row.errors.length && !overwriteFlash,
                            'border-amber-200 overwrite-flash': row.is_duplicate && !row.errors.length && overwriteFlash,
                            'border-gray-200': !row.is_duplicate && !row.errors.length,
                        }">
                        <div class="flex items-center justify-between gap-2">
                            <span class="body-small-semibold truncate">{{ row.name || "(empty)" }}</span>
                            <span v-if="row.errors.length > 0" class="shrink-0 details px-1.5 py-0.5 rounded-full bg-red-100 text-red-700">
                                {{ row.errors.length === 1 ? row.errors[0] : `${row.errors.length} issues` }}
                            </span>
                            <span v-else-if="row.is_duplicate" class="shrink-0 details px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700"> Duplicate </span>
                            <span v-else class="shrink-0 details px-1.5 py-0.5 rounded-full bg-green-100 text-green-700"> New </span>
                        </div>
                        <div v-if="row.email || row.job_title" class="mt-1 details text-gray-500 truncate">
                            <span v-if="row.email">{{ row.email }}</span>
                            <span v-if="row.email && row.job_title"> · </span>
                            <span v-if="row.job_title">{{ row.job_title }}</span>
                        </div>
                        <!-- Segmented control for duplicates -->
                        <div v-if="row.is_duplicate && !row.errors.length && !autoOverwrite" class="flex justify-end mt-2" role="radiogroup" :aria-label="`Action for ${row.name}`">
                            <div class="inline-flex rounded-md border border-gray-300 overflow-hidden details">
                                <button
                                    type="button"
                                    class="px-3 py-1 transition-colors"
                                    :class="rowActions[row.row_index] === 'skip' ? 'bg-gray-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
                                    @click="rowActions[row.row_index] = 'skip'">
                                    Skip
                                </button>
                                <button
                                    type="button"
                                    class="px-3 py-1 border-l border-gray-300 transition-colors"
                                    :class="rowActions[row.row_index] === 'overwrite' ? 'bg-gray-700 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'"
                                    @click="rowActions[row.row_index] = 'overwrite'">
                                    Overwrite
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Desktop: table -->
                <div class="hidden md:block max-h-80 overflow-y-auto border border-valence-grey-300 rounded-lg">
                    <table class="w-full body-small">
                        <thead class="bg-gray-50 sticky top-0">
                            <tr>
                                <th class="text-left p-2 font-medium text-gray-400 w-8">#</th>
                                <th class="text-left p-2 font-medium">Name</th>
                                <th class="text-left p-2 font-medium">Email</th>
                                <th class="text-left p-2 font-medium">Job Title</th>
                                <th class="text-left p-2 font-medium">Status</th>
                                <th v-if="!autoOverwrite" class="text-left p-2 font-medium">Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="row in previewData.rows"
                                :key="row.row_index"
                                :class="{
                                    'bg-red-50': row.errors.length > 0,
                                    'bg-amber-50': row.is_duplicate && !row.errors.length && !overwriteFlash,
                                    'overwrite-flash': row.is_duplicate && !row.errors.length && overwriteFlash,
                                }">
                                <td class="p-2 text-gray-400">{{ row.row_index + 1 }}</td>
                                <td class="p-2">{{ row.name || "(empty)" }}</td>
                                <td class="p-2 text-gray-500">{{ row.email || "-" }}</td>
                                <td class="p-2 text-gray-500">{{ row.job_title || "-" }}</td>
                                <td class="p-2">
                                    <template v-if="row.errors.length > 1">
                                        <BaseTooltip side="top">
                                            <template #trigger>
                                                <span class="text-red-600 cursor-help underline decoration-dotted">{{ row.errors.length }} issues</span>
                                            </template>
                                            <template #content>
                                                <ul class="details list-disc pl-3 py-1 space-y-0.5">
                                                    <li v-for="(err, idx) in row.errors" :key="idx">{{ err }}</li>
                                                </ul>
                                            </template>
                                        </BaseTooltip>
                                    </template>
                                    <template v-else-if="row.errors.length === 1">
                                        <BaseTooltip side="top">
                                            <template #trigger>
                                                <span class="text-red-600 cursor-help underline decoration-dotted">{{ row.errors[0] }}</span>
                                            </template>
                                            <template #content>
                                                <span class="details">{{ row.errors[0] }}</span>
                                            </template>
                                        </BaseTooltip>
                                    </template>
                                    <span v-else-if="row.is_duplicate && autoOverwrite" class="text-amber-600">Will be updated</span>
                                    <span v-else-if="row.is_duplicate" class="text-amber-600">Duplicate of "{{ row.existing_connection_name }}"</span>
                                    <span v-else class="text-green-600">New</span>
                                </td>
                                <td v-if="!autoOverwrite" class="p-2">
                                    <!-- eslint-disable-next-line vuejs-accessibility/form-control-has-label -->
                                    <select
                                        v-if="row.is_duplicate && !row.errors.length"
                                        v-model="rowActions[row.row_index]"
                                        :aria-label="`Action for ${row.name}`"
                                        class="body-small border border-gray-300 rounded px-1 py-0.5">
                                        <option value="skip">Skip</option>
                                        <option value="overwrite">Overwrite</option>
                                    </select>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <div class="flex justify-end gap-3 sticky bottom-0 bg-white pt-3 pb-4 md:relative md:pb-0 -mx-4 px-4 md:mx-0 md:px-0 border-t border-gray-100 md:border-t-0">
                    <BaseButton theme="tertiary" @click="resetToUpload">Back</BaseButton>
                    <BaseButton theme="primary" :disabled="isImporting || !hasImportableRows" @click="handleImport">
                        {{ isImporting ? "Importing..." : "Confirm Import" }}
                    </BaseButton>
                </div>
            </template>

            <!-- Step 3: Importing (progress) -->
            <template v-else-if="step === 'importing'">
                <div class="text-center py-8">
                    <div class="overflow-hidden mb-3"><i class="bi bi-arrow-repeat text-3xl text-gray-400 block animate-spin" aria-hidden="true"></i></div>
                    <h3 class="title-body mb-2">Importing...</h3>
                    <template v-if="importProgress">
                        <p class="body-small text-gray-500 mb-4">{{ importProgress.processed }} of {{ importProgress.total }} rows processed</p>
                        <BaseProgressBar :value="progressPercent" :aria-label="`${importProgress.processed} of ${importProgress.total} rows processed`" />
                    </template>
                </div>
            </template>

            <!-- Step 4: Result -->
            <template v-else-if="step === 'result'">
                <div class="text-center py-4">
                    <i class="bi bi-check-circle text-4xl text-green-500 mb-3 block" aria-hidden="true"></i>
                    <h3 class="title-body mb-2">Import Complete</h3>
                    <p class="body-small text-gray-600 mb-3">{{ resultSummaryText }}</p>
                    <div class="flex flex-wrap justify-center gap-3 body-small text-gray-600 mb-4">
                        <span v-if="importResult.created" class="px-2 py-1 rounded bg-green-50 text-green-700"> {{ importResult.created }} created </span>
                        <span v-if="importResult.updated" class="px-2 py-1 rounded bg-amber-50 text-amber-700"> {{ importResult.updated }} updated </span>
                        <span v-if="importResult.skipped" class="px-2 py-1 rounded bg-gray-100 text-gray-600"> {{ importResult.skipped }} skipped (duplicates) </span>
                        <span v-if="importResult.errors.length" class="px-2 py-1 rounded bg-red-50 text-red-700"> {{ importResult.errors.length }} errors </span>
                    </div>
                    <details v-if="importResult.errors.length" class="text-left mb-4 mx-auto max-w-md">
                        <summary class="body-small text-red-600 cursor-pointer hover:text-red-700">Show error details</summary>
                        <ul class="mt-2 space-y-1 body-small text-gray-600">
                            <li v-for="(error, idx) in importResult.errors" :key="idx" class="px-3 py-1.5 bg-red-50 rounded text-red-700">
                                Row {{ error.row }}: {{ error.message }}
                            </li>
                        </ul>
                    </details>
                    <BaseButton theme="primary" @click="handleDone">Done</BaseButton>
                </div>
            </template>
        </div>
    </BaseDialog>
</template>

<script setup>
import { useIntervalFn } from "@vueuse/core";
import { fetchApi, useFetch } from "~vue/api";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseProgressBar from "~vue/components/BaseProgressBar.vue";
import BaseTooltip from "~vue/components/BaseTooltip.vue";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { computed, ref, watch } from "vue";

const EXPECTED_COLUMNS = ["name", "job_title", "email", "in_team", "in_org", "comfort_level", "notes"];
const POLL_INTERVAL_MS = 1000;
const POLL_TIMEOUT_MS = 5 * 60 * 1000;

const props = defineProps({
    csvTemplateUrl: { type: String, required: true },
    csvPreviewUrl: { type: String, required: true },
    csvImportUrl: { type: String, required: true },
});

const emit = defineEmits(["imported"]);
const isOpen = defineModel("open", { type: Boolean, default: false });

const step = ref("upload");
const selectedFile = ref(null);
const isDragging = ref(false);
const autoOverwrite = ref(false);
const uploadError = ref("");
const fileInput = ref(null);
const previewData = ref(null);
const rowActions = ref({});
const importResult = ref(null);
const isUploading = ref(false);
const isImporting = ref(false);
const parsedFileInfo = ref(null);
const overwriteFlash = ref(false);
const columnRefOpen = ref(false);
const importProgress = ref(null);
const pollStatusUrl = ref(null);
const pollStartTime = ref(null);
const expectedColumns = EXPECTED_COLUMNS;

const hasImportableRows = computed(() => {
    if (!previewData.value) {
        return false;
    }
    return previewData.value.rows.some((row) => !row.errors.length);
});

const truncatedFileName = computed(() => {
    if (!selectedFile.value) {
        return "";
    }
    const name = selectedFile.value.name;
    const MAX_LENGTH = 40;
    if (name.length <= MAX_LENGTH) {
        return name;
    }
    return `${name.slice(0, MAX_LENGTH / 2)}...${name.slice(name.length - MAX_LENGTH / 2)}`;
});

const formattedFileSize = computed(() => {
    if (!selectedFile.value) {
        return "";
    }
    const size = selectedFile.value.size;
    if (size < 1024) {
        return `${size} B`;
    } else if (size < 1024 * 1024) {
        return `${Math.floor(size / 1024)} KB`;
    }
    return `${Math.floor(size / (1024 * 1024))} MB`;
});

const missingExpectedColumns = computed(() => {
    if (!parsedFileInfo.value) {
        return [];
    }
    const detected = parsedFileInfo.value.detectedColumns;
    return EXPECTED_COLUMNS.filter((col) => !detected.includes(col));
});

const resultSummaryText = computed(() => {
    if (!importResult.value) {
        return "";
    }
    const { created, updated } = importResult.value;
    if (created && updated) {
        return `${created} ${created === 1 ? "person" : "people"} added and ${updated} updated.`;
    }
    if (created) {
        return `${created} ${created === 1 ? "person" : "people"} added to your list.`;
    }
    if (updated) {
        return `${updated} ${updated === 1 ? "record" : "records"} updated.`;
    }
    return "No changes were made.";
});

const progressPercent = computed(() => {
    if (!importProgress.value || !importProgress.value.total) {
        return 0;
    }
    return Math.round((importProgress.value.processed / importProgress.value.total) * 100);
});

const { pause: pausePoll, resume: resumePoll } = useIntervalFn(
    async () => {
        if (!pollStatusUrl.value) {
            return;
        }

        if (Date.now() - pollStartTime.value > POLL_TIMEOUT_MS) {
            pausePoll();
            uploadError.value = "Import timed out. Please try again.";
            step.value = "preview";
            logUserInteraction(AmplitudeEventNames.CSV_IMPORT_FAILED, {
                error_message: "timeout",
            });
            return;
        }

        const { data } = await useFetch(pollStatusUrl.value).get().json();
        if (!data.value) {
            return;
        }

        importProgress.value = data.value;

        if (data.value.status === "completed") {
            pausePoll();
            importResult.value = {
                created: data.value.created,
                updated: data.value.updated,
                skipped: data.value.skipped,
                errors: data.value.errors,
            };
            step.value = "result";
            logUserInteraction(AmplitudeEventNames.CSV_IMPORT_SUCCESS, {
                created: data.value.created,
                updated: data.value.updated,
                skipped: data.value.skipped,
                error_count: data.value.errors?.length || 0,
            });
        } else if (data.value.status === "failed") {
            pausePoll();
            uploadError.value = data.value.error_message || "Import failed. Please try again.";
            step.value = "preview";
            logUserInteraction(AmplitudeEventNames.CSV_IMPORT_FAILED, {
                error_message: data.value.error_message || "Import failed",
            });
        }
    },
    POLL_INTERVAL_MS,
    { immediate: false },
);

const triggerFileInput = () => {
    fileInput.value?.click();
};

const downloadTemplate = async () => {
    const response = await fetchApi(props.csvTemplateUrl);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "my_people_template.csv";
    a.click();
    URL.revokeObjectURL(url);
    logUserInteraction(AmplitudeEventNames.CSV_IMPORT_TEMPLATE_DOWNLOADED);
};

const parseFileHeaders = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
        const text = e.target?.result;
        if (typeof text !== "string") {
            return;
        }
        const lines = text.split(/\r?\n/).filter((line) => line.trim());
        if (lines.length === 0) {
            return;
        }
        const headers = lines[0].split(",").map((h) => h.trim().toLowerCase().replace(/^"|"$/g, ""));
        parsedFileInfo.value = {
            rowCount: lines.length - 1,
            detectedColumns: headers,
        };
    };
    reader.readAsText(file);
};

const MAX_FILE_SIZE = 1 * 1024 * 1024; // 1MB

const validateFileSize = (file) => {
    if (file.size > MAX_FILE_SIZE) {
        uploadError.value = "File size exceeds 1 MB limit.";
        return false;
    }
    return true;
};

const handleFileSelect = (event) => {
    const file = event.target.files?.[0];
    if (file) {
        if (!validateFileSize(file)) {
            return;
        }
        selectedFile.value = file;
        uploadError.value = "";
        columnRefOpen.value = false;
        parseFileHeaders(file);
        logUserInteraction(AmplitudeEventNames.CSV_IMPORT_FILE_SELECTED, {
            file_size: file.size,
        });
    }
};

const handleDrop = (event) => {
    isDragging.value = false;
    const file = event.dataTransfer.files?.[0];
    if (file && file.name.endsWith(".csv")) {
        if (!validateFileSize(file)) {
            return;
        }
        selectedFile.value = file;
        uploadError.value = "";
        columnRefOpen.value = false;
        parseFileHeaders(file);
        logUserInteraction(AmplitudeEventNames.CSV_IMPORT_FILE_SELECTED, {
            file_size: file.size,
        });
    } else {
        uploadError.value = "Please drop a CSV file.";
    }
};

const clearFile = () => {
    selectedFile.value = null;
    parsedFileInfo.value = null;
    uploadError.value = "";
    if (fileInput.value) {
        fileInput.value.value = "";
    }
};

const handleUpload = async () => {
    if (!selectedFile.value) {
        return;
    }
    isUploading.value = true;
    uploadError.value = "";

    const formData = new FormData();
    formData.append("file", selectedFile.value);

    const { data, error } = await useFetch(props.csvPreviewUrl).post(formData).json();

    isUploading.value = false;

    if (error.value) {
        const errorData = data.value;
        uploadError.value = errorData?.error || "Failed to process CSV file. Please try again.";
        return;
    }

    previewData.value = data.value;

    // Initialize row actions for duplicates
    const actions = {};
    for (const row of previewData.value.rows) {
        if (row.is_duplicate) {
            actions[row.row_index] = "overwrite";
        }
    }
    rowActions.value = actions;

    step.value = "preview";
    logUserInteraction(AmplitudeEventNames.CSV_IMPORT_PREVIEW_SHOWN, {
        row_count: previewData.value.rows.length,
        new_count: previewData.value.summary.new,
        duplicate_count: previewData.value.summary.duplicates,
        error_count: previewData.value.summary.errors,
    });
};

const handleImport = async () => {
    if (!previewData.value) {
        return;
    }
    isImporting.value = true;

    const rows = previewData.value.rows
        .filter((row) => !row.errors.length)
        .map((row) => {
            const isDuplicate = row.is_duplicate;
            const shouldOverwrite = autoOverwrite.value || rowActions.value[row.row_index] === "overwrite";
            const shouldSkip = isDuplicate && !shouldOverwrite;

            return {
                row_index: row.row_index,
                name: row.name,
                email: row.email,
                job_title: row.job_title,
                in_team: row.in_team,
                in_org: row.in_org,
                comfort_level: row.comfort_level,
                notes: row.notes,
                existing_connection_id: row.existing_connection_id,
                overwrite: isDuplicate && shouldOverwrite,
                skip: shouldSkip,
            };
        });

    const payload = ref({ rows });
    const { data, error } = await useFetch(props.csvImportUrl).post(payload).json();

    isImporting.value = false;

    if (error.value) {
        uploadError.value = "Import failed. Please try again.";
        return;
    }

    pollStatusUrl.value = data.value.status_url;
    importProgress.value = { processed: 0, total: rows.length };
    pollStartTime.value = Date.now();
    step.value = "importing";
    logUserInteraction(AmplitudeEventNames.CSV_IMPORT_SUBMITTED, {
        total_rows: rows.length,
        overwrite_count: rows.filter((r) => r.overwrite).length,
        skip_count: rows.filter((r) => r.skip).length,
    });
    resumePoll();
};

const resetToUpload = () => {
    step.value = "upload";
    previewData.value = null;
    rowActions.value = {};
    autoOverwrite.value = false;
};

const handleDone = () => {
    isOpen.value = false;
    emit("imported", importResult.value);
};

// Flash duplicate rows when auto-overwrite is toggled on
watch(autoOverwrite, (newVal) => {
    if (newVal && previewData.value?.summary.duplicates > 0) {
        overwriteFlash.value = true;
        setTimeout(() => {
            overwriteFlash.value = false;
        }, 800);
    }
});

// Reset state when dialog closes
watch(isOpen, (newVal, oldVal) => {
    if (newVal) {
        logUserInteraction(AmplitudeEventNames.CSV_IMPORT_DIALOG_OPENED);
    }
    if (oldVal && !newVal && step.value !== "result") {
        logUserInteraction(AmplitudeEventNames.CSV_IMPORT_CANCELLED, {
            step: step.value,
        });
    }
    if (!newVal) {
        pausePoll();
        step.value = "upload";
        selectedFile.value = null;
        autoOverwrite.value = false;
        uploadError.value = "";
        previewData.value = null;
        rowActions.value = {};
        importResult.value = null;
        isUploading.value = false;
        isImporting.value = false;
        isDragging.value = false;
        parsedFileInfo.value = null;
        overwriteFlash.value = false;
        columnRefOpen.value = false;
        importProgress.value = null;
        pollStatusUrl.value = null;
        pollStartTime.value = null;
    }
});
</script>

<style scoped>
.overwrite-flash {
    animation: amber-flash 0.8s ease-out;
}

@keyframes amber-flash {
    0% {
        background-color: rgb(251 191 36 / 0.4);
    }
    100% {
        background-color: rgb(255 251 235);
    }
}
</style>
